import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Card, CardContent } from "@/components/ui/card";
import { ChevronLeft, ChevronRight, Star, Quote } from "lucide-react";
import { useTestimonials } from "@/hooks/useTestimonials";

const TestimonialsSection = () => {
  const { t } = useTranslation();
  const [activeIndex, setActiveIndex] = useState(0);
  const { testimonials, isLoading } = useTestimonials();

  const nextTestimonial = () => {
    setActiveIndex((prev) => (prev + 1) % testimonials.length);
  };

  const prevTestimonial = () => {
    setActiveIndex((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  };

  if (isLoading || testimonials.length === 0) {
    return null;
  }

  return (
    <section id="reviews" className="py-24 bg-secondary/30">
      <div className="container mx-auto px-4">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <span className="inline-block px-4 py-1.5 rounded-full bg-hawk-orange/10 text-hawk-orange text-sm font-semibold mb-4">
            {t("testimonials.badge")}
          </span>
          <h2 className="text-4xl md:text-5xl font-display font-bold mb-6">
            {t("testimonials.heading")}
          </h2>
          <p className="text-lg text-muted-foreground">
            {t("testimonials.subtitle")}
          </p>
        </div>

        <div className="max-w-4xl mx-auto">
          {/* Main testimonial card */}
          <Card variant="testimonial" className="relative">
            <CardContent className="p-8 md:p-12">
              {/* Quote icon */}
              <div className="absolute top-6 right-6 w-16 h-16 rounded-full bg-hawk-orange/10 flex items-center justify-center">
                <Quote className="text-hawk-orange" size={32} />
              </div>

              {/* Rating */}
              <div className="flex gap-1 mb-6">
                {[...Array(testimonials[activeIndex].rating)].map((_, i) => (
                  <Star key={i} className="text-hawk-gold fill-hawk-gold" size={20} />
                ))}
              </div>

              {/* Quote */}
              <blockquote className="text-xl md:text-2xl leading-relaxed mb-8 text-foreground/90">
                "{testimonials[activeIndex].quote}"
              </blockquote>

              {/* Author */}
              <div className="flex items-center justify-between">
                <div>
                  <div className="font-display font-bold text-lg">{testimonials[activeIndex].name}</div>
                  <div className="text-muted-foreground">{testimonials[activeIndex].role}</div>
                  <div className="inline-block mt-2 px-3 py-1 rounded-full bg-hawk-orange/10 text-hawk-orange text-xs font-semibold">
                    {testimonials[activeIndex].plan} {t("testimonials.plan")}
                  </div>
                </div>

                {/* Navigation */}
                <div className="flex gap-2">
                  <button
                    onClick={prevTestimonial}
                    className="w-12 h-12 rounded-full border-2 border-border flex items-center justify-center hover:border-hawk-orange hover:text-hawk-orange transition-colors"
                  >
                    <ChevronLeft size={20} />
                  </button>
                  <button
                    onClick={nextTestimonial}
                    className="w-12 h-12 rounded-full border-2 border-border flex items-center justify-center hover:border-hawk-orange hover:text-hawk-orange transition-colors"
                  >
                    <ChevronRight size={20} />
                  </button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Dots indicator */}
          <div className="flex justify-center gap-2 mt-8">
            {testimonials.map((_, index) => (
              <button
                key={index}
                onClick={() => setActiveIndex(index)}
                className={`w-2.5 h-2.5 rounded-full transition-all duration-300 ${
                  index === activeIndex
                    ? "bg-hawk-orange w-8"
                    : "bg-border hover:bg-hawk-orange/50"
                }`}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default TestimonialsSection;
